import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { logError } from 'commons/logger';
import PostHogFeatureFlagsContext from './PostHogFeatureFlagsContext';
const postHogOverrideState = {
  hasOverridden: false
};
const reportedExposures = new Set();
const POSTHOG_INIT_MAX_WAIT_MS = 10_000;
const POSTHOG_INIT_POLL_MS = 100;
const getCurrentViewKey = () => `${window.location.pathname}${window.location.search}`;
const parsePostHogQuery = query => {
  try {
    const queryObject = JSON.parse(query);
    if (queryObject?.clear === true) {
      return false;
    }
    return queryObject;
  } catch (err) {
    logError(err, 'Unable to parse posthogOverrides query', {
      query
    });

    // clear overrides in for any malformed query
    return false;
  }
};
const applyPostHogOverrides = posthog => {
  const postHogOverrideQuery = new URLSearchParams(window.location.search).get('posthogOverrides');
  if (postHogOverrideState.hasOverridden || !postHogOverrideQuery || postHogOverrideQuery === '' || postHogOverrideQuery === '{}' || !posthog?.featureFlags) {
    return;
  }
  const parsedQuery = parsePostHogQuery(postHogOverrideQuery);
  if (parsedQuery !== false) {
    postHogOverrideState.hasOverridden = true;
    posthog.featureFlags.override(parsedQuery);
  }
};
const readLiveFlagState = (posthog, name) => {
  if (!posthog?.isFeatureEnabled(name)) {
    return {
      value: null,
      payload: null
    };
  }
  const val = posthog.getFeatureFlag(name);
  const payloadValue = posthog.getFeatureFlagPayload(name);
  if (val === null) {
    return {
      value: null,
      payload: null
    };
  }
  return {
    value: val,
    payload: payloadValue
  };
};
const buildExposureKey = (posthog, name, distinctId) => {
  const resolvedDistinctId = distinctId || posthog?.get_distinct_id?.() || 'unknown-distinct-id';
  return `${name}::${resolvedDistinctId}::${getCurrentViewKey()}`;
};
const reportFeatureFlagExposure = (posthog, name, distinctId) => {
  if (!posthog) {
    return;
  }
  const exposureKey = buildExposureKey(posthog, name, distinctId);
  if (reportedExposures.has(exposureKey)) {
    return;
  }
  reportedExposures.add(exposureKey);
  posthog.getFeatureFlag(name);
};
const waitForPostHog = onReady => {
  if (window.posthog) {
    onReady(window.posthog);
    return () => {};
  }
  const startedAt = Date.now();
  const intervalId = setInterval(() => {
    if (window.posthog) {
      clearInterval(intervalId);
      onReady(window.posthog);
      return;
    }
    if (Date.now() - startedAt >= POSTHOG_INIT_MAX_WAIT_MS) {
      clearInterval(intervalId);
    }
  }, POSTHOG_INIT_POLL_MS);
  return () => {
    clearInterval(intervalId);
  };
};
const usePostHogFeatureFlagWithPayload = name => {
  const $ = _c(24);
  const seededContext = useContext(PostHogFeatureFlagsContext);
  const seededValue = seededContext?.featureFlags?.[name];
  const seededDistinctId = seededContext?.distinctId;
  let t0;
  if ($[0] !== name || $[1] !== seededContext?.featureFlags) {
    let t1;
    if ($[3] !== seededContext?.featureFlags) {
      t1 = seededContext?.featureFlags || {};
      $[3] = seededContext?.featureFlags;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = Object.prototype.hasOwnProperty.call(t1, name);
    $[0] = name;
    $[1] = seededContext?.featureFlags;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasSeededValue = t0;
  let t1;
  if ($[5] !== hasSeededValue || $[6] !== seededValue) {
    t1 = () => hasSeededValue ? seededValue : null;
    $[5] = hasSeededValue;
    $[6] = seededValue;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const [value, setValue] = useState(t1);
  const [payload, setPayload] = useState(null);
  let t2;
  let t3;
  if ($[8] !== hasSeededValue || $[9] !== seededValue) {
    t2 = () => {
      if (hasSeededValue) {
        setValue(seededValue);
      }
    };
    t3 = [hasSeededValue, seededValue];
    $[8] = hasSeededValue;
    $[9] = seededValue;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[12] !== hasSeededValue || $[13] !== name) {
    t4 = () => {
      if (hasSeededValue) {
        return;
      }
      const removePostHogEventListeners = [];
      const subscribeToPostHog = posthog => {
        applyPostHogOverrides(posthog);
        const removePostHogEventListener = posthog.onFeatureFlags(() => {
          applyPostHogOverrides(posthog);
          const nextState = readLiveFlagState(posthog, name);
          if (nextState.value !== null) {
            setValue(nextState.value);
            setPayload(nextState.payload);
          }
        });
        removePostHogEventListeners.push(removePostHogEventListener);
        const initialState = readLiveFlagState(posthog, name);
        if (initialState.value !== null) {
          setValue(initialState.value);
          setPayload(initialState.payload);
        }
      };
      const cancelWait = waitForPostHog(subscribeToPostHog);
      return () => {
        cancelWait();
        removePostHogEventListeners.forEach(_temp);
      };
    };
    t5 = [hasSeededValue, name];
    $[12] = hasSeededValue;
    $[13] = name;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[16] !== hasSeededValue || $[17] !== name || $[18] !== seededDistinctId) {
    t6 = () => {
      if (!hasSeededValue || typeof window === "undefined") {
        return;
      }
      const reportExposure = posthog_0 => {
        applyPostHogOverrides(posthog_0);
        reportFeatureFlagExposure(posthog_0, name, seededDistinctId);
      };
      if (window.posthog) {
        reportExposure(window.posthog);
        return;
      }
      return waitForPostHog(reportExposure);
    };
    t7 = [hasSeededValue, name, seededDistinctId];
    $[16] = hasSeededValue;
    $[17] = name;
    $[18] = seededDistinctId;
    $[19] = t6;
    $[20] = t7;
  } else {
    t6 = $[19];
    t7 = $[20];
  }
  useEffect(t6, t7);
  let t8;
  if ($[21] !== payload || $[22] !== value) {
    t8 = [value, payload];
    $[21] = payload;
    $[22] = value;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
export const resetPostHogFeatureFlagTestState = () => {
  postHogOverrideState.hasOverridden = false;
  reportedExposures.clear();
};
export default usePostHogFeatureFlagWithPayload;
function _temp(removePostHogEventListener_0) {
  removePostHogEventListener_0?.();
}